import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import "../assets/panels.css";
import "../assets/datatables.css";
import styles from "../assets/propertyViewingsIndex.module.css";
import Loader from "../components/Loader";

const PropertyViewingsIndex = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewingsAll, setViewingsAll] = useState(0);
  const [viewingsUpcoming, setViewingsUpcoming] = useState(0);
  const [viewingsPast, setViewingsPast] = useState(0);
  const [viewingsCompleted, setViewingsCompleted] = useState(0);
  const [viewingsUncompleted, setViewingsUncompleted] = useState(0);
  const [users, setUsers] = useState({});
  const [selectedUserId, setSelectedUserId] = useState("");
  const [clickedStatusId, setClickedStatusId] = useState(0); // Upcoming by default
  const [viewingsData, setViewingsData] = useState([]);
  const [filteredViewings, setFilteredViewings] = useState([]);
  const [showEntries, setShowEntries] = useState(50); // Default 50 per page
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    perPage: 50,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const [showColumnModal, setShowColumnModal] = useState(false);
  const columnDropdownRef = useRef(null);
  const [columnVisibility, setColumnVisibility] = useState({
    property: true,
    id: true,
    postcode: true,
    availableRoomsNumber: true,
    availableRoomsNames: true,
    first: true,
    last: true,
    day: true,
    from: true,
    to: true,
    confirmed: true,
    guardianNamesConfirmed: true,
    attendees: true,
    guardianNamesAttendees: true,
    taken: true,
    guardianNamesTaken: true,
    calendar: true,
    actions: true,
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (columnDropdownRef.current && !columnDropdownRef.current.contains(event.target)) {
        setShowColumnModal(false);
      }
    };

    if (showColumnModal) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showColumnModal]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        console.log(
          `🔍 [FRONTEND] Fetching property viewings index data - page ${currentPage}, limit ${showEntries}`
        );

        // Add timeout to prevent hanging requests
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(
            () => reject(new Error("Request timeout after 60 seconds")),
            60000
          );
        });

        // Include pagination and status parameters in API call
        const apiPromise = api.get("/propertyviewings", {
          params: {
            page: currentPage,
            limit: showEntries,
            status: clickedStatusId, // Pass status filter to backend
          },
        });

        const response = await Promise.race([apiPromise, timeoutPromise]);
        
        console.log(
          "📊 [FRONTEND] Property viewings index response:",
          response.data
        );
        console.log(
          "📊 [FRONTEND] Response status:",
          response.status,
          "Response headers:",
          response.headers
        );

        // Safely stringify response for logging (handle circular references)
        try {
          console.log(
            "📊 [FRONTEND] Full response object:",
            JSON.stringify(response.data, null, 2)
          );
        } catch (stringifyError) {
          console.warn("⚠️ [FRONTEND] Could not stringify response:", stringifyError);
          console.log("📊 [FRONTEND] Response data type:", typeof response.data);
        }

        // Handle different response structures (production vs staging)
        let responseData = response.data;
        
        // Check if response is wrapped in another data property
        if (responseData && responseData.data && typeof responseData.data === 'object' && !Array.isArray(responseData.data)) {
          responseData = responseData.data;
        }

        // Check for success flag or assume success if data exists
        const isSuccess = responseData.success === true || 
                         (responseData.success !== false && 
                          (responseData.viewingsAll !== undefined || 
                           (responseData.data && (responseData.data.viewingsAll !== undefined || Array.isArray(responseData.data.viewings)))));

        if (isSuccess) {
          // Handle nested data structure
          const data = responseData.data || responseData;
          
          // Validate data structure
          if (!data || (typeof data !== 'object')) {
            throw new Error("Invalid data structure received from server");
          }
          
          setViewingsAll(data.viewingsAll || 0);
          setViewingsUpcoming(data.viewingsUpcoming || 0);
          setViewingsPast(data.viewingsPast || 0);
          setViewingsCompleted(data.viewingsCompleted || 0);
          setViewingsUncompleted(data.viewingsUncompleted || 0);
          setUsers(data.users || {});
          setViewingsData(Array.isArray(data.viewings) ? data.viewings : []);
          setFilteredViewings(Array.isArray(data.viewings) ? data.viewings : []);

          // Set pagination metadata if available
          if (data.pagination) {
            setPagination(data.pagination);
            console.log(
              `✅ [FRONTEND] Pagination: page ${data.pagination.currentPage} of ${data.pagination.totalPages} (${data.pagination.totalCount} total)`
            );
          }

          console.log(
            "✅ [FRONTEND] Property viewings index data loaded successfully"
          );
        } else {
          // Log the actual response structure for debugging
          console.error(
            "❌ [FRONTEND] Invalid response structure:",
            {
              success: responseData.success,
              hasData: !!responseData.data,
              hasViewingsAll: responseData.viewingsAll !== undefined,
              responseKeys: Object.keys(responseData || {}),
              responseType: typeof responseData,
            }
          );
          throw new Error(
            responseData.message || 
            responseData.error || 
            "Failed to fetch property viewings index data - invalid response structure"
          );
        }

        setLoading(false);
        setError(null); // Clear any previous errors
      } catch (err) {
        console.error(
          "❌ [FRONTEND] Error fetching property viewings index:",
          err
        );
        console.error(
          "❌ [FRONTEND] Error details:",
          {
            message: err.message,
            name: err.name,
            stack: err.stack,
            response: err.response?.data,
            status: err.response?.status,
            statusText: err.response?.statusText,
            code: err.code, // Network error codes
          }
        );
        
        // More specific error messages
        let errorMessage = "Failed to load property viewings.";
        if (err.message?.includes("timeout")) {
          errorMessage = "Request timed out. The server may be processing a large amount of data. Please try again.";
        } else if (err.code === "ERR_NETWORK" || err.code === "ECONNABORTED") {
          errorMessage = "Network error. Please check your connection and try again.";
        } else if (err.response?.status === 500) {
          errorMessage = "Server error. Please contact support if this persists.";
        } else if (err.response?.data?.message) {
          errorMessage = err.response.data.message;
        } else if (err.response?.data?.error) {
          errorMessage = err.response.data.error;
        } else if (err.message) {
          errorMessage = err.message;
        }
        
        setError(errorMessage);
        setLoading(false);
      }
    };

    fetchData();
  }, [currentPage, showEntries, clickedStatusId]); // Refetch when page, entries per page, or status changes

  const handleStatusClick = (statusId) => {
    console.log("🎯 [FRONTEND] Status clicked:", statusId);
    // Just update the status - the useEffect will re-fetch with server-side filtering
    setClickedStatusId(statusId);
    // Note: Server-side filtering is now applied via the status query parameter
    // The useEffect dependency on clickedStatusId will trigger a re-fetch
  };

  const handleUserCalendarChange = (event) => {
    const userId = event.target.value;
    if (userId && userId !== "Please select") {
      console.log("👤 [FRONTEND] Navigating to user calendar:", userId);
      navigate(`/property_viewings/user/${userId}`);
    }
  };

  const handleAddViewingClick = () => {
    console.log("🔗 [FRONTEND] Navigating to create property viewings page");
    navigate("/property_viewings/create");
  };

  // Column labels for display (used in exports and column visibility UI)
  const columnLabels = {
    property: "Property",
    id: "ID",
    postcode: "Postcode",
    availableRoomsNumber: "Available rooms number",
    availableRoomsNames: "Available rooms names",
    first: "First",
    last: "Last",
    day: "Day",
    from: "From",
    to: "To",
    confirmed: "Confirmed",
    guardianNamesConfirmed: "Guardian Names (Confirmed)",
    attendees: "Attendees",
    guardianNamesAttendees: "Guardian Names (Attendees)",
    taken: "Taken",
    guardianNamesTaken: "Guardian Names (Taken)",
    calendar: "Calendar",
    actions: "Actions",
  };

  // Column data mapping for exports (maps column key to data accessor)
  const columnDataMap = {
    property: (viewing) => viewing.property?.name || "",
    id: (viewing) => viewing.property?.property_id || "",
    postcode: (viewing) => viewing.property?.postcode || "",
    availableRoomsNumber: (viewing) => viewing.available_rooms_number || "",
    availableRoomsNames: (viewing) => viewing.available_rooms_names || "",
    first: (viewing) => viewing.user?.first_name || "",
    last: (viewing) => viewing.user?.last_name || "",
    day: (viewing) => viewing.day || "",
    from: (viewing) => viewing.time_from || "",
    to: (viewing) => viewing.time_to || "",
    confirmed: (viewing) => viewing.confirmed || 0,
    guardianNamesConfirmed: (viewing) => viewing.guardian_names_confirmed || "",
    attendees: (viewing) => viewing.attendees ?? "",
    guardianNamesAttendees: (viewing) => viewing.guardian_names_attendees || "",
    taken: (viewing) => viewing.taken ?? "",
    guardianNamesTaken: (viewing) => viewing.guardian_names_taken || "",
  };

  // Get exportable columns (exclude calendar and actions which are UI-only)
  const getExportableColumns = () => {
    const nonExportableColumns = ["calendar", "actions"];
    return Object.keys(columnVisibility).filter(
      (key) => columnVisibility[key] && !nonExportableColumns.includes(key)
    );
  };

  const handleExportCSV = () => {
    console.log("📊 [FRONTEND] Exporting as CSV");

    // Helper function to escape CSV values (wrap in quotes and escape internal quotes)
    const escapeCSV = (value) => {
      if (value === null || value === undefined) return "";
      const str = String(value);
      // If value contains comma, newline, or quote, wrap in quotes and escape internal quotes
      if (str.includes(",") || str.includes("\n") || str.includes('"')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    // Get visible exportable columns
    const visibleColumns = getExportableColumns();

    // Create CSV content based on visible columns
    const csvContent = [
      // Header row - use column labels
      visibleColumns.map((key) => columnLabels[key] || key),
      // Data rows
      ...filteredViewings.map((viewing) =>
        visibleColumns.map((key) => {
          const accessor = columnDataMap[key];
          const value = accessor ? accessor(viewing) : "";
          return escapeCSV(value);
        })
      ),
    ]
      .map((row) => row.join(","))
      .join("\n");

    // Download CSV file with BOM for Excel compatibility
    const BOM = "\uFEFF";
    const blob = new Blob([BOM + csvContent], { type: "text/csv;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "property_viewings.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleExportPDF = async () => {
    try {
      console.log("📄 [FRONTEND] Starting PDF export...");

      // Get visible exportable columns first to determine orientation
      const visibleColumns = getExportableColumns();
      const columnCount = visibleColumns.length;

      // Create PDF - use landscape for many columns
      const { jsPDF } = await import("jspdf");
      const autoTable = (await import("jspdf-autotable")).default;

      const orientation = columnCount > 6 ? "landscape" : "portrait";
      const doc = new jsPDF({ orientation });

      const margin = 10;

      // Add title
      doc.setFontSize(14);
      doc.text("Property Viewings Report", margin, 15);
      doc.setFontSize(9);
      doc.text(`Generated: ${new Date().toLocaleDateString("en-GB")}`, margin, 22);
      doc.text(`Records: ${filteredViewings.length}`, margin + 80, 22);

      // Status summary (compact, single line)
      doc.setFontSize(8);
      doc.text(
        `All: ${viewingsAll} | Upcoming: ${viewingsUpcoming} | Past: ${viewingsPast} | Completed: ${viewingsCompleted} | Uncompleted: ${viewingsUncompleted}`,
        margin,
        30
      );

      // Get headers from visible columns
      const headers = visibleColumns.map((key) => columnLabels[key] || key);

      // Build table data
      const tableData = filteredViewings.map((viewing) =>
        visibleColumns.map((key) => {
          const accessor = columnDataMap[key];
          const value = accessor ? accessor(viewing) : "";
          return String(value);
        })
      );

      // Use autoTable for proper table rendering with text wrapping
      autoTable(doc, {
        head: [headers],
        body: tableData,
        startY: 35,
        styles: {
          fontSize: 7,
          cellPadding: 2,
          overflow: "linebreak", // Wrap text to next line
          cellWidth: "wrap",
        },
        headStyles: {
          fillColor: [66, 66, 66],
          textColor: [255, 255, 255],
          fontStyle: "bold",
          halign: "left",
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245],
        },
        columnStyles: visibleColumns.reduce((styles, key, index) => {
          // Set specific widths for certain columns - narrow columns get fixed width
          if (key === "from" || key === "to") {
            styles[index] = { cellWidth: 11 };
          } else if (key === "confirmed" || key === "attendees" || key === "taken") {
            styles[index] = { cellWidth: 12 };
          } else if (key === "availableRoomsNumber") {
            styles[index] = { cellWidth: 14 };
          } else if (key === "day") {
            styles[index] = { cellWidth: 20 };
          } else if (key === "postcode") {
            styles[index] = { cellWidth: 16 };
          } else if (key === "first" || key === "last") {
            styles[index] = { cellWidth: 18 };
          } else if (key === "id") {
            styles[index] = { cellWidth: 16 };
          } else if (key === "guardianNamesConfirmed" || key === "guardianNamesAttendees" || key === "guardianNamesTaken") {
            // Guardian names columns need more space - use auto width with minimum
            styles[index] = { cellWidth: "auto", minCellWidth: 40 };
          }
          // Other columns (property, availableRoomsNames) will auto-size
          return styles;
        }, {}),
        margin: { left: margin, right: margin },
        didDrawPage: (data) => {
          // Add page number at bottom
          const pageCount = doc.getNumberOfPages();
          doc.setFontSize(8);
          doc.text(
            `Page ${data.pageNumber} of ${pageCount}`,
            doc.internal.pageSize.width / 2,
            doc.internal.pageSize.height - 10,
            { align: "center" }
          );
        },
      });

      // Save PDF
      doc.save(
        `property-viewings-${new Date().toISOString().split("T")[0]}.pdf`
      );

      console.log("PDF exported successfully!");
    } catch (error) {
      console.error("Export PDF error:", error);
      alert(`Failed to export PDF: ${error.message}`);
    }
  };

  const handleColumnVisibility = () => {
    console.log("👁️ [FRONTEND] Toggle column visibility");
    setShowColumnModal(!showColumnModal);
  };

  const toggleColumnVisibility = (columnKey) => {
    setColumnVisibility((prev) => ({
      ...prev,
      [columnKey]: !prev[columnKey],
    }));
  };

  const handleViewUserCalendar = (userId) => {
    console.log("📅 [FRONTEND] Viewing user calendar for user:", userId);
    navigate(`/property_viewings/user/${userId}`);
  };

  const handleViewPropertyCalendar = (propertyId) => {
    console.log("📅 [FRONTEND] Viewing property calendar for property:", propertyId);
    navigate(`/property_viewings/property/${propertyId}`);
  };

  const handleEditViewing = (viewingId) => {
    console.log("✏️ [FRONTEND] Editing viewing:", viewingId);
    navigate(`/property_viewings/${viewingId}/edit`);
  };

  const handleDeleteViewing = async (viewingId) => {
    console.log("🗑️ [FRONTEND] Deleting viewing:", viewingId);

    const confirmMessage = `Are you sure you want to delete this viewing?\n\nThis will permanently delete:\n• The viewing record\n• All guardian confirmations\n• All notification subscriptions\n\nThis action cannot be undone.`;

    if (window.confirm(confirmMessage)) {
      try {
        console.log("🗑️ [FRONTEND] Calling delete API for viewing:", viewingId);

        const response = await api.delete(`/propertyviewings/${viewingId}`);
        console.log("📊 [FRONTEND] Delete response:", response.data);

        if (response.data.success) {
          console.log("✅ [FRONTEND] Viewing deleted successfully");

          // Show detailed success message
          const deletedRecords = response.data.data?.deleted_records || [];
          const totalTypes =
            response.data.data?.total_record_types_deleted || 0;

          let successMessage = `Viewing deleted successfully!\n\nDeleted records:\n`;
          deletedRecords.forEach((recordType) => {
            successMessage += `• ${recordType}\n`;
          });
          successMessage += `\nTotal record types deleted: ${totalTypes}`;

          alert(successMessage);

          // Remove from local state
          const updatedViewings = filteredViewings.filter(
            (v) => v.id !== viewingId
          );
          setFilteredViewings(updatedViewings);
          setViewingsData((prev) => prev.filter((v) => v.id !== viewingId));
        } else {
          throw new Error(response.data.message || "Failed to delete viewing");
        }
      } catch (err) {
        console.error("❌ [FRONTEND] Error deleting viewing:", err);
        alert(
          `Failed to delete viewing: ${
            err.response?.data?.message || err.message
          }`
        );
      }
    }
  };

  const getStatusColor = (statusId) => {
    switch (statusId) {
      case 999:
        return "#5bc0de"; // All
      case 0:
        return "#28a745"; // Upcoming
      case 1:
        return "#ffc107"; // Past
      case 2:
        return "#cc6600"; // Completed
      case 3:
        return "midnightblue"; // Uncompleted
      default:
        return "#6c757d";
    }
  };

  // Search functionality
  const handleSearch = (value) => {
    setSearchTerm(value);
    setCurrentPage(1); // Reset to first page when searching
    const filtered = viewingsData.filter(
      (viewing) =>
        viewing.property?.name?.toLowerCase().includes(value.toLowerCase()) ||
        viewing.property?.property_id
          ?.toLowerCase()
          .includes(value.toLowerCase()) ||
        viewing.user?.first_name?.toLowerCase().includes(value.toLowerCase()) ||
        viewing.user?.last_name?.toLowerCase().includes(value.toLowerCase()) ||
        viewing.day?.includes(value) ||
        viewing.time_from?.includes(value) ||
        viewing.time_to?.includes(value)
    );
    setFilteredViewings(filtered);
  };

  // Handle status click - reset to first page
  const handleStatusClickWithPagination = (statusId) => {
    setCurrentPage(1); // Reset to first page when changing status
    handleStatusClick(statusId);
  };

  // Server-side pagination - use data directly from API (no client-side slicing)
  // The backend already returns paginated data, so we use it directly
  const paginatedViewings = filteredViewings; // Already paginated from backend
  const totalPages = pagination.totalPages || 1;
  const totalCount = pagination.totalCount || 0;

  // Handle page change
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      // Scroll to top of table
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Reset to first page when entries per page changes
  const handleEntriesChange = (value) => {
    setShowEntries(Number(value));
    setCurrentPage(1);
  };

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return (
      <div className="container-fluid">
        <div className="alert alert-danger">{error}</div>
      </div>
    );
  }

  return (
    <div
      className="container-fluid"
      style={{ backgroundColor: "white", padding: "10px 0", margin: "0 auto" }}
    >
      {/* Breadcrumbs */}
      <div className="breadcrumb-row">
        <span
          className="breadcrumb-main"
          style={{ fontWeight: 400, fontSize: "14px", color: "black", cursor: 'pointer' }}
          onClick={() => navigate('/dashboard')}
        >
          Dashboard
        </span>
        <div
          style={{ background: "#30C693", fontWeight: 400, fontSize: "14px" }}
        >
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-properties">Property Viewings</span>
        </div>
      </div>

      {/* Status Overview */}
      <div
        className="row"
        style={{ backgroundColor: "#F8F9FA", margin: "16px", }}
      >
        <div
          className="col-xs-12"
          style={{ border: "none", boxShadow: "none" }}
        >
          <div
            className="panel-title title-standard"
            style={{
              border: "none",
              boxShadow: "none",
              background: "transparent",
            }}
          >
            <h3>Status overview</h3>
          </div>
          <div
            className="panel-group mb-20"
            style={{
              border: "none",
              boxShadow: "none",
              background: "transparent",
            }}
          >
            <div className="col-md-2">
              <div
                className="activity-box js-property-viewing-overview-box"
                style={{ backgroundColor: getStatusColor(999) }}
                data-status_id="999"
                onClick={() => handleStatusClickWithPagination(999)}
              >
                <p className="activity-value">{viewingsAll}</p>
                <p className="activity-title" data-to_display="all">
                  All
                </p>
              </div>
            </div>

            <div className="col-md-2">
              <div
                className="activity-box js-property-viewing-overview-box"
                style={{ backgroundColor: getStatusColor(0) }}
                data-status_id="0"
                onClick={() => handleStatusClickWithPagination(0)}
              >
                <p className="activity-value">{viewingsUpcoming}</p>
                <p className="activity-title" data-to_display="Upcoming">
                  Upcoming
                </p>
              </div>
            </div>

            <div className="col-md-2">
              <div
                className="activity-box js-property-viewing-overview-box"
                style={{ backgroundColor: getStatusColor(1) }}
                data-status_id="1"
                onClick={() => handleStatusClickWithPagination(1)}
              >
                <p className="activity-value">{viewingsPast}</p>
                <p className="activity-title" data-to_display="Past">
                  Past
                </p>
              </div>
            </div>

            <div className="col-md-2">
              <div
                className="activity-box js-property-viewing-overview-box"
                style={{ backgroundColor: getStatusColor(2) }}
                data-status_id="2"
                onClick={() => handleStatusClickWithPagination(2)}
              >
                <p className="activity-value">{viewingsCompleted}</p>
                <p className="activity-title" data-to_display="Completed">
                  Completed
                </p>
              </div>
            </div>

            <div className="col-md-2">
              <div
                className="activity-box js-property-viewing-overview-box"
                style={{ backgroundColor: getStatusColor(3) }}
                data-status_id="3"
                onClick={() => handleStatusClickWithPagination(3)}
              >
                <p className="activity-value">{viewingsUncompleted}</p>
                <p className="activity-title" data-to_display="Uncompleted">
                  Uncompleted
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Go to User Calendar */}
      <div className="row">
        <div
          className=""
          style={{
            display: "flex",
            alignItems: "center",
            flexDirection: "row",
            paddingTop: "16px",
            paddingLeft: "0px",
            margin: "0",
            padding: "10px 30px",
          }}
        >
          <div
            className="property-viewings-go-to-user-calendar-group"
            style={{
              display: "flex",
              alignItems: "center",
              flexDirection: "row",
            }}
          >
            <span style={{ width: "300px" }}>
              <strong>Go to user calendar: </strong>
            </span>
            <select
              className="form-control js-pv-go-to-user-calendar"
              value={selectedUserId}
              onChange={handleUserCalendarChange}
            >
              <option value="">Please select</option>
              {Object.entries(users).map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="row my-4" style={{ padding: "0", margin: "0" }}>
        <div className="col-md-12">
          <div className="panel panel-default panel-standard">
            <div
              style={{ margin: "0" }}
              id="property-status-span"
              className="panel-heading panel-title"
            >
              <h3 className="mr-auto" style={{ margin: "0" }}>
                Property Viewings
              </h3>
              <div className="panel-actions">
                <button
                  style={{ background: "#17264B", color: "white" }}
                  className="btn btn-secondary"
                  onClick={handleAddViewingClick}
                >
                  <i className="glyphicon glyphicon-plus"></i> Add Viewings
                </button>
              </div>
            </div>
            <div className="panel-body">
              <input
                type="hidden"
                name="clickedStatusId"
                value={clickedStatusId}
              />

              {/* Data Controls */}
              <div className="data-controls mb-3">
                <div className="row align-items-center">
                  <div className="col-md-3">
                    <div className="d-flex align-items-center">
                      <label className="mb-0 me-2">Show</label>
                      <select
                        className="form-control form-control-sm"
                        value={showEntries}
                        onChange={(e) =>
                          handleEntriesChange(e.target.value)
                        }
                        style={{
                          width: "70px",
                          margin: "0 5px",
                        }}
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                      <label className="mb-0 ms-1">entries</label>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="d-flex align-items-center">
                      <label className="mb-0 me-2">Search:</label>
                      <input

                        className="form-control form-control-sm"
                        type="text"
                        value={searchTerm}
                        onChange={(e) => handleSearch(e.target.value)}
                        style={{
                          width: "300px",
                        }}
                        placeholder="Search viewings..."
                      />
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="d-flex justify-content-end">
                      <button
                        className="btn btn-sm btn-default me-2"
                        onClick={handleExportCSV}
                        style={{
                          border: "1px solid #ccc",
                          backgroundColor: "#f8f9fa",
                        }}
                      >
                        Export as CSV
                      </button>
                      <button
                        className="btn btn-sm btn-default me-2"
                        onClick={handleExportPDF}
                        style={{
                          border: "1px solid #ccc",
                          backgroundColor: "#f8f9fa",
                        }}
                      >
                        Export as PDF
                      </button>
                      <div ref={columnDropdownRef} style={{ position: "relative", display: "inline-block" }}>
                        <button
                          className="btn btn-sm btn-default"
                          onClick={handleColumnVisibility}
                          style={{
                            border: "1px solid #ccc",
                            backgroundColor: "#f8f9fa",
                          }}
                        >
                          Column visibility
                        </button>
                        {showColumnModal && (
                          <div
                            style={{
                              position: "absolute",
                              top: "100%",
                              right: 0,
                              backgroundColor: "white",
                              border: "1px solid #ccc",
                              borderRadius: "4px",
                              boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
                              zIndex: 1000,
                              minWidth: "200px",
                              maxHeight: "400px",
                              overflowY: "auto",
                              padding: "10px",
                            }}
                          >
                            <div style={{ fontWeight: "bold", marginBottom: "10px", borderBottom: "1px solid #eee", paddingBottom: "5px" }}>
                              Toggle Columns
                            </div>
                            {Object.keys(columnLabels).map((key) => (
                              <div
                                key={key}
                                style={{
                                  padding: "5px 0",
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                }}
                                onClick={() => toggleColumnVisibility(key)}
                              >
                                <input
                                  type="checkbox"
                                  checked={columnVisibility[key]}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    toggleColumnVisibility(key);
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  style={{ marginRight: "8px" }}
                                />
                                <span>{columnLabels[key]}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {filteredViewings.length > 0 ? (
                <div className="dataTable_wrapper">
                  <table
                    id="datatable-property-viewings"
                    className="table table-hover dt-responsive"
                  >
                    <thead>
                      <tr style={{ fontSize: "11px" }}>
                        {columnVisibility.property && <th>Property</th>}
                        {columnVisibility.id && <th>ID</th>}
                        {columnVisibility.postcode && <th>Postcode</th>}
                        {columnVisibility.availableRoomsNumber && (
                          <th style={{ fontSize: "9px" }}>
                            Available rooms number
                          </th>
                        )}
                        {columnVisibility.availableRoomsNames && (
                          <th style={{ fontSize: "9px" }}>
                            Available rooms names
                          </th>
                        )}
                        {columnVisibility.first && <th>First</th>}
                        {columnVisibility.last && <th>Last</th>}
                        {columnVisibility.day && <th>Day</th>}
                        {columnVisibility.from && (
                          <th
                            className="js-tooltip"
                            data-toggle="tooltip"
                            data-placement="top"
                            title="Time From"
                            data-container="body"
                          >
                            From
                          </th>
                        )}
                        {columnVisibility.to && (
                          <th
                            className="js-tooltip"
                            data-toggle="tooltip"
                            data-placement="top"
                            title="Time To"
                            data-container="body"
                          >
                            To
                          </th>
                        )}
                        {columnVisibility.confirmed && <th style={{ textAlign: 'center' }}>Confirmed</th>}
                        {columnVisibility.guardianNamesConfirmed && <th style={{ textAlign: 'center', minWidth: '150px', maxWidth: '250px' }}>Guardian Names (Confirmed)</th>}
                        {columnVisibility.attendees && <th style={{ textAlign: 'center' }}>Attendees</th>}
                        {columnVisibility.guardianNamesAttendees && <th style={{ textAlign: 'center', minWidth: '150px', maxWidth: '250px' }}>Guardian Names (Attendees)</th>}
                        {columnVisibility.taken && (
                          <th
                            className="js-tooltip"
                            data-toggle="tooltip"
                            data-placement="top"
                            title="Rooms Taken"
                            data-container="body"
                            style={{ textAlign: 'center' }}
                          >
                            Taken
                          </th>
                        )}
                        {columnVisibility.guardianNamesTaken && <th style={{ textAlign: 'center', minWidth: '150px', maxWidth: '250px' }}>Guardian Names (Taken)</th>}
                        {columnVisibility.calendar && <th style={{ textAlign: 'center' }}>Calendar</th>}
                        {columnVisibility.actions && <th className="action-head">Actions</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedViewings.length > 0 ? (
                        paginatedViewings.map((viewing, index) => (
                          <tr key={viewing.id || index}>
                            {columnVisibility.property && <td>{viewing.property?.name || ""}</td>}
                            {columnVisibility.id && <td>{viewing.property?.property_id || ""}</td>}
                            {columnVisibility.postcode && <td>{viewing.property?.postcode || ""}</td>}
                            {columnVisibility.availableRoomsNumber && <td>{viewing.available_rooms_number || ""}</td>}
                            {columnVisibility.availableRoomsNames && <td>{viewing.available_rooms_names || ""}</td>}
                            {columnVisibility.first && <td>{viewing.user?.first_name || ""}</td>}
                            {columnVisibility.last && <td>{viewing.user?.last_name || ""}</td>}
                            {columnVisibility.day && <td>{viewing.day || ""}</td>}
                            {columnVisibility.from && <td>{viewing.time_from || ""}</td>}
                            {columnVisibility.to && <td>{viewing.time_to || ""}</td>}
                            {columnVisibility.confirmed && (
                              <td style={{ textAlign: 'center' }}>
                                <span
                                  onClick={() => handleEditViewing(viewing.id)}
                                  style={{ cursor: 'pointer', fontWeight: 'bold', color: '#337ab7' }}
                                  title="Click to edit viewing"
                                >
                                  {viewing.confirmed || 0}
                                </span>
                              </td>
                            )}
                            {columnVisibility.guardianNamesConfirmed && (
                              <td style={{ textAlign: 'center', maxWidth: '250px', wordWrap: 'break-word', whiteSpace: 'normal' }}>{viewing.guardian_names_confirmed || ""}</td>
                            )}
                            {columnVisibility.attendees && (
                              <td style={{ textAlign: 'center' }}>
                                <span
                                  onClick={() => handleEditViewing(viewing.id)}
                                  style={{ cursor: 'pointer', fontWeight: 'bold', color: '#337ab7' }}
                                  title="Click to edit viewing"
                                >
                                  {viewing.attendees || ""}
                                </span>
                              </td>
                            )}
                            {columnVisibility.guardianNamesAttendees && (
                              <td style={{ textAlign: 'center', maxWidth: '250px', wordWrap: 'break-word', whiteSpace: 'normal' }}>{viewing.guardian_names_attendees || ""}</td>
                            )}
                            {columnVisibility.taken && <td style={{ textAlign: 'center' }}>{viewing.taken || ""}</td>}
                            {columnVisibility.guardianNamesTaken && (
                              <td style={{ textAlign: 'center', maxWidth: '250px', wordWrap: 'break-word', whiteSpace: 'normal' }}>{viewing.guardian_names_taken || ""}</td>
                            )}
                            {columnVisibility.calendar && (
                              <td style={{ textAlign: 'center' }}>
                                <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                                  {viewing.property?.id && (
                                    <button
                                      className="btn btn-sm btn-info"
                                      onClick={() =>
                                        handleViewPropertyCalendar(viewing.property.id)
                                      }
                                      title="View Property Calendar"
                                      style={{ fontSize: "11px" }}
                                    >
                                      Property
                                    </button>
                                  )}
                                  {viewing.user?.id && (
                                    <button
                                      className="btn btn-sm btn-warning"
                                      onClick={() =>
                                        handleViewUserCalendar(viewing.user.id)
                                      }
                                      title="View User Calendar"
                                      style={{ fontSize: "11px" }}
                                    >
                                      User
                                    </button>
                                  )}
                                </div>
                              </td>
                            )}
                            {columnVisibility.actions && (
                              <td className="action-head">
                                <div className="btn-group">
                                  <button
                                    className="btn btn-sm btn-info"
                                    onClick={() => handleEditViewing(viewing.id)}
                                    title="Edit"
                                  >
                                    <i className="glyphicon glyphicon-edit">
                                      Edit
                                    </i>
                                  </button>
                                  <button
                                    className="btn btn-sm btn-danger"
                                    onClick={() =>
                                      handleDeleteViewing(viewing.id)
                                    }
                                    title="Delete"
                                  >
                                    <i className="glyphicon glyphicon-trash">
                                      Delete
                                    </i>
                                  </button>
                                </div>
                              </td>
                            )}
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={Object.values(columnVisibility).filter(Boolean).length} className="text-center">
                            No viewings found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>

                  {/* Pagination Controls */}
                  {filteredViewings.length > 0 && (
                    <div className="row" style={{ marginTop: "20px" }}>
                      <div className="col-sm-5">
                        <div className="dataTables_info" style={{ paddingTop: "8px" }}>
                          Showing {((currentPage - 1) * showEntries) + 1} to {Math.min(currentPage * showEntries, totalCount)} of {totalCount.toLocaleString()} entries
                        </div>
                      </div>
                      <div className="col-sm-7">
                        <div className="dataTables_paginate paging_simple_numbers" style={{ textAlign: "right" }}>
                          <ul className="pagination" style={{ margin: "0" }}>
                            <li className={`paginate_button previous ${!pagination.hasPrevPage ? 'disabled' : ''}`}>
                              <button
                                className="btn btn-sm btn-default"
                                onClick={() => {
                                  if (pagination.hasPrevPage) {
                                    handlePageChange(currentPage - 1);
                                  }
                                }}
                                disabled={!pagination.hasPrevPage}
                                style={{
                                  border: "1px solid #ddd",
                                  padding: "6px 12px",
                                  marginRight: "2px",
                                  cursor: !pagination.hasPrevPage ? 'not-allowed' : 'pointer',
                                  opacity: !pagination.hasPrevPage ? 0.6 : 1
                                }}
                              >
                                Previous
                              </button>
                            </li>
                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                              let page;
                              if (totalPages <= 5) {
                                page = i + 1;
                              } else if (currentPage <= 3) {
                                page = i + 1;
                              } else if (currentPage >= totalPages - 2) {
                                page = totalPages - 4 + i;
                              } else {
                                page = currentPage - 2 + i;
                              }
                              
                              if (page > totalPages) return null;
                              
                              return (
                                <li key={page} className={`paginate_button ${currentPage === page ? 'active' : ''}`}>
                                  <button
                                    className="btn btn-sm btn-default"
                                    onClick={() => handlePageChange(page)}
                                    style={{
                                      border: "1px solid #ddd",
                                      padding: "6px 12px",
                                      marginRight: "2px",
                                      backgroundColor: currentPage === page ? "#337ab7" : "white",
                                      color: currentPage === page ? "white" : "#337ab7",
                                      cursor: "pointer"
                                    }}
                                  >
                                    {page}
                                  </button>
                                </li>
                              );
                            })}
                            <li className={`paginate_button next ${!pagination.hasNextPage ? 'disabled' : ''}`}>
                              <button
                                className="btn btn-sm btn-default"
                                onClick={() => {
                                  if (pagination.hasNextPage) {
                                    handlePageChange(currentPage + 1);
                                  }
                                }}
                                disabled={!pagination.hasNextPage}
                                style={{
                                  border: "1px solid #ddd",
                                  padding: "6px 12px",
                                  marginLeft: "2px",
                                  cursor: !pagination.hasNextPage ? 'not-allowed' : 'pointer',
                                  opacity: !pagination.hasNextPage ? 0.6 : 1
                                }}
                              >
                                Next
                              </button>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div>No viewings!</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyViewingsIndex;
